<script setup>
/**
 * The policy card as a policyholder sees it, with the editor's current name,
 * icon and colour in it.
 *
 * Everything around those three — the policy number, the address, the renewal
 * ring — is fixed sample data, and deliberately so: this is here to answer "is
 * that the right name and the right look", not to preview somebody's actual
 * policy. The ring is inline rather than the `chat/shared/CircleProgress` component
 * because that one has fixed sizes and no centre label, and bending it for a
 * static illustration would be changing a real control to serve a mock.
 */
import PolicyLineIcon from './PolicyLineIcon.vue';
import { DEFAULT_COLOR, UNTITLED } from '../../helpers/policyLines.js';

const props = defineProps({
  name: { type: String, default: '' },
  icon: { type: String, default: 'shield' },
  color: { type: String, default: DEFAULT_COLOR },
});

const RING = { circumference: 2 * Math.PI * 18, percent: 93, color: '#8A5200' };
const offset = RING.circumference * (1 - RING.percent / 100);
</script>

<template>
  <div class="policy-card">
    <div
      class="policy-card__art"
      :style="{ background: `linear-gradient(160deg, ${props.color}14, ${props.color}26)`, color: props.color }"
    >
      <PolicyLineIcon :name="props.icon" :size="40" />
    </div>

    <div class="policy-card__body">
      <div class="policy-card__ident">
        <b class="policy-card__name">{{ props.name || UNTITLED }}</b>
        <div class="policy-card__meta">HO-••••4821 · 1420 N Lincoln Ave</div>
      </div>

      <div class="policy-card__renewal">
        <div class="policy-card__ring">
          <svg width="44" height="44" aria-hidden="true">
            <circle cx="22" cy="22" r="18" :stroke="`${RING.color}22`" stroke-width="4" fill="none" />
            <circle
              cx="22" cy="22" r="18"
              :stroke="RING.color" stroke-width="4" fill="none" stroke-linecap="round"
              :stroke-dasharray="RING.circumference" :stroke-dashoffset="offset"
            />
          </svg>
          <span :style="{ color: RING.color }">24d</span>
        </div>
        <div>
          <b>Renews September 6th, 2026</b>
          <div class="policy-card__meta">24 days away</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.policy-card {
  overflow: hidden;
  border-radius: var(--xl-radius-md, 18px);
  background: var(--xl-color-surface, #fff);
  box-shadow: var(--xl-shadow-2, 0 6px 20px rgba(20, 16, 24, 0.1));
  font-family: var(--xl-font-family);

  &__art {
    height: 140px;
    display: grid;
    place-items: center;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 22px 24px 24px;
  }

  &__ident {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  &__name {
    display: block;
    font-size: 19px;
    color: var(--xl-color-primary-text);
  }

  &__meta {
    font-size: var(--xl-type-body-small-size, 13px);
    color: var(--xl-color-outline);
  }

  &__renewal {
    display: flex;
    gap: 13px;
    align-items: center;

    b { font-size: 14px; }
  }

  &__renewal > div {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  /*
    The dial and its label are stacked in one grid cell, so the label centres
    against the ring. It used to be `position: absolute` with no offsets, which
    takes it out of the grid entirely and drops it at the box's top-left corner
    — over the rim rather than inside it.
  */
  &__ring {
    display: grid;
    place-items: center;
    flex: 0 0 44px;

    > * { grid-area: 1 / 1; }

    svg { transform: rotate(-90deg); }

    span {
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
    }
  }
}
</style>
